import { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

/**
 * MEMORIA DE SESIÓN DE LA BANDEJA — filtros, búsqueda y ancla de vuelta.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * QUÉ PROBLEMA RESUELVE (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * `/chats` y `/chat/:chatID` son rutas HERMANAS (`App.js`), así que abrir un chat
 * **desmonta `Chats` entero** y destruye todo su `useState`: los siete filtros, la
 * búsqueda, el rango de fechas, el panel abierto y la posición del scroll. Volver es
 * `navigate('/chats')` —una navegación nueva, no un `back`— así que ni el navegador
 * intenta restaurar nada, y la app no tiene `ScrollRestoration`.
 *
 * Este proveedor envuelve LAS DOS rutas y sobrevive el viaje.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ EN MEMORIA, Y ESO ES LO QUE DA EL «EMPIEZA LIMPIO» GRATIS
 * ───────────────────────────────────────────────────────────────────────────────
 * Sin `localStorage`, sin `sessionStorage`, sin URL. Consecuencias, todas deseadas:
 *
 *   bandeja → chat → bandeja     se conserva  (el proveedor sigue montado)
 *   bandeja → Dashboard → vuelta LIMPIO       (`/dashboard` está fuera: se desmonta)
 *   recargar / cerrar ventana    LIMPIO       (memoria del proceso)
 *   pestaña nueva                LIMPIA e independiente
 *
 * **El desmontaje ES el reset**, así que no hay código de limpieza que pueda olvidarse de
 * un campo el día que se añada el octavo filtro. Con `localStorage` habría que escribirlo
 * y mantenerlo, y además la URL haría compartible un estado que es de sesión.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL ANCLA SE CONSUME Y SE BORRA
 * ───────────────────────────────────────────────────────────────────────────────
 * `chatIdDeVuelta` lo escribe la bandeja al navegar a un chat, y `Chats` lo LEE Y LO
 * BORRA al usarlo. Si sobreviviera, una segunda entrada a la bandeja desde el Dashboard
 * saltaría a un chat que el operador ya no tiene en la cabeza.
 *
 * Lo escribe la BANDEJA y no el detalle: al detalle se llega desde varios sitios y sólo
 * el de la bandeja significa «volveré aquí».
 */

const Contexto = createContext(null);

/** Los valores de partida. En un objeto para que el reseteo sea uno y no doce. */
const FILTROS_INICIALES = {
  estadoFiltro: 'todos',
  canal: 'todos',
  cuenta: 'todas',       // commit 10: la cuenta del canal ('todas' | accountId); sólo se enseña con >1 cuenta
  busqueda: '',
  dateRange: { start: '', end: '' },
  messageTypeFilter: 'todos',
  showUnreadOnly: false,
  soloRequierenAtencion: false,
  soloConVenta: false,
  soloConAgenda: false,
  soloConTraspaso: false,
  soloBloqueados: false,
  soloConCotizacion: false,          // F, 3-nov-2026: `utils/cotizacionEnviada.js`
  soloCotizacionSinRespuesta: false,
  showAdvancedFilters: false,
};

export function useSesionDeBandeja() {
  const ctx = useContext(Contexto);
  // Null y no un objeto vacío: un componente fuera del proveedor tiene que romperse al
  // montarlo, no comportarse como si no hubiera filtros. Ese fallo sería invisible.
  if (!ctx) throw new Error('useSesionDeBandeja fuera de <SesionDeBandeja>');
  return ctx;
}

/**
 * EL ESTADO, SIN NADA DEL ENRUTADOR.
 *
 * ⚠️ VIVE APARTE POR UNA RAZÓN DE PRUEBAS, NO DE ESTILO. El jest de CRA **no resuelve
 * `react-router-dom`** en este repositorio —es la causa de que `App.test.js` sea la única
 * suite roja, y lo es desde antes de esto—. Si el estado viviera junto al `<Outlet />`,
 * importarlo en un test fallaría al cargar el módulo y esta pieza se quedaría sin guarda.
 *
 * Es el mismo criterio que sacó `destinoDelPrivateReply` de `igWebhook` y la fusión de
 * chats del proveedor: lo que hay que poder probar no se mezcla con lo que no se puede
 * cargar en un test.
 */
/**
 * LOS FILTROS QUE POSEE EL PANEL AVANZADO, y por qué la lista existe.
 *
 * El botón «Limpiar filtros» vive dentro de ese panel y sólo debe limpiar lo que el panel
 * enseña: `estadoFiltro`, `canal` y `busqueda` están en la barra de arriba y limpiarlos
 * desde ahí sorprendería.
 *
 * ⚠️ PERO LA LISTA NO ES LA FUENTE DE LOS VALORES: los valores salen de
 * `FILTROS_INICIALES`. Esto sólo dice CUÁLES, no A QUÉ. Antes el botón enumeraba nombre y
 * valor a mano y se quedó sin limpiar `soloConAgenda` ni `soloConTraspaso` — los dos
 * últimos que se añadieron, exactamente el fallo que se repite con cada marcador nuevo.
 *
 * `cubreTodosLosFiltros` obliga a decidir: un filtro nuevo en `FILTROS_INICIALES` que no
 * esté ni aquí ni en `FUERA_DEL_PANEL` pone el test en rojo.
 */
const FILTROS_DEL_PANEL = [
  'dateRange', 'messageTypeFilter', 'showUnreadOnly', 'soloRequierenAtencion',
  'soloConVenta', 'soloConAgenda', 'soloConTraspaso', 'soloBloqueados',
  'soloConCotizacion', 'soloCotizacionSinRespuesta',
];

/** Lo que NO limpia ese botón, y por qué cada uno. */
const FUERA_DEL_PANEL = [
  'estadoFiltro',        // vive en la barra de arriba, con su propio selector
  'canal',               // idem
  'cuenta',              // idem: va junto al canal
  'busqueda',            // idem, y borrarla desde el panel sería desconcertante
  'showAdvancedFilters', // no es un filtro: es si el panel está abierto
];

/**
 * ¿Hay algún filtro puesto? Se DERIVA de los iniciales, no se enumera.
 *
 * ⚠️ `showAdvancedFilters` SE EXCLUYE, y no es un detalle: abrir el panel no es filtrar.
 * Contarlo dispararía el agotado de páginas —hasta 296 documentos— por el mero hecho de
 * desplegar el panel para mirar.
 */
export function hayAlgunFiltro(filtros) {
  // `cuenta` tampoco cuenta: la aplica la CONSULTA (`datosDeBandeja`), no un filtro en memoria;
  // contarla dispararía el agotado de páginas por elegir un número.
  return Object.keys(FILTROS_INICIALES)
    .filter((k) => k !== 'showAdvancedFilters' && k !== 'cuenta')
    .some((k) => JSON.stringify(filtros[k]) !== JSON.stringify(FILTROS_INICIALES[k]));
}

/**
 * ¿ESTÁ DISPONIBLE EL DATO QUE NECESITAN LOS FILTROS PUESTOS?
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL CASO QUE LA PRODUJO (24-sep-2026, al TERCER intento fallido)
 * ───────────────────────────────────────────────────────────────────────────────
 * Con «Solo con agenda» puesto, el ancla se descartaba siempre. El log lo dio en una
 * línea:
 *
 *     [ancla] DESCARTAR { cargados: 297, visibles: 0, enCargados: true, … }
 *
 * El chat estaba cargado y la lista visible estaba VACÍA. Causa: **ocho de los nueve
 * filtros leen el documento del chat** —su dato llega con el chat— y `soloConAgenda` lee
 * `reservasPorChat`, que viene de un `getDocs` APARTE. El oyente entrega los 297 chats en
 * milisegundos; las reservas tardan más. En esa ventana ningún chat cumple el filtro, la
 * lista parece terminada y vacía, y el ancla se descarta sin haber tenido oportunidad.
 *
 * ⚠️ Y EL MISMO HUECO SE LE ENSEÑA AL OPERADOR: durante ese instante la bandeja dice
 * «no hay chats» con el filtro de agenda puesto. Es el mismo defecto visto por el otro
 * lado, y se arregla en el mismo sitio.
 *
 * Medido: `soloConAgenda` es HOY el único filtro con esa forma. Esta función existe para
 * que el siguiente no vuelva a costar tres intentos — un filtro nuevo cuyo dato llegue
 * por separado se declara aquí y queda cubierto.
 */
export function datosDeFiltrosListos(filtros, disponibilidad = {}) {
  if (filtros?.soloConAgenda && !disponibilidad.reservasCargadas) return false;
  return true;
}

/** Control de integridad: cada filtro declarado está clasificado. Lo usa el test. */
export function cubreTodosLosFiltros() {
  const declarados = new Set([...FILTROS_DEL_PANEL, ...FUERA_DEL_PANEL]);
  return Object.keys(FILTROS_INICIALES).filter((k) => !declarados.has(k));
}

export function useEstadoDeBandeja() {
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);

  /**
   * EL ANCLA VA EN UN `ref`, NO EN ESTADO, y por dos razones:
   *
   *   · no tiene que provocar repintado — se lee UNA vez, cuando la lista ya tiene
   *     contenido, y pintar de nuevo por guardarla sería trabajo para nada;
   *   · leerla y borrarla es SÍNCRONO. Con estado habría que leer el valor dentro de un
   *     actualizador, que en StrictMode corre dos veces en desarrollo: el ancla se
   *     consumiría distinto en desarrollo y en producción.
   */
  const anclaRef = useRef(null);

  /** Cambia un filtro por nombre. Un solo `setter` para los doce. */
  const setFiltro = useCallback((nombre, valor) => {
    setFiltros((prev) => (prev[nombre] === valor ? prev : { ...prev, [nombre]: valor }));
  }, []);

  /**
   * Lee el ancla Y LA BORRA. Devuelve null si no había.
   *
   * La lectura destructiva vive aquí y no en el llamante: si `Chats` tuviera que
   * acordarse de borrarla, el día que alguien añada un segundo consumidor se aplicaría
   * dos veces.
   */
  const consumirAncla = useCallback(() => {
    const valor = anclaRef.current;
    anclaRef.current = null;
    return valor;
  }, []);

  const marcarVuelta = useCallback((chatId) => { anclaRef.current = chatId || null; }, []);

  /**
   * MIRA el ancla SIN consumirla.
   *
   * Existe porque consumirla antes de usarla era un defecto real: con un filtro activo la
   * lista sigue agotando páginas cuando el efecto corre, el nodo todavía no está pintado,
   * y el ancla se gastaba sin haber servido. Quien la mira decide después si la gasta.
   */
  const verAncla = useCallback(() => anclaRef.current, []);

  /**
   * Devuelve los filtros del PANEL a su valor inicial.
   *
   * Se DERIVA de `FILTROS_INICIALES`: el botón ya no enumera valores, así que un filtro
   * nuevo queda cubierto en cuanto se añade a `FILTROS_DEL_PANEL` — y si alguien olvida
   * añadirlo, `cubreTodosLosFiltros` lo delata en el test.
   */
  const limpiarFiltrosDelPanel = useCallback(() => {
    setFiltros((prev) => {
      const siguiente = { ...prev };
      FILTROS_DEL_PANEL.forEach((k) => { siguiente[k] = FILTROS_INICIALES[k]; });
      return siguiente;
    });
  }, []);

  return useMemo(() => ({
    filtros, setFiltro, marcarVuelta, consumirAncla, verAncla, limpiarFiltrosDelPanel,
  }), [filtros, setFiltro, marcarVuelta, consumirAncla, verAncla, limpiarFiltrosDelPanel]);
}

export { Contexto, FILTROS_DEL_PANEL, FUERA_DEL_PANEL };
export { FILTROS_INICIALES };
